import { schemaExtensionRegistry } from '../../DocsApp/plugins/schemaExtensionRegistry';
import { citationMarkSpec } from './citationMark';
import { bibliographyNodeSpec } from './bibliographyNode';
import { citationTooltipPlugin } from './citationTooltipPlugin';
import { citationCleanupPlugin } from './citationCleanupPlugin';
import { createFileLogger } from '../../../utils/logger';
import { ReferenceEntity } from '../types/reference';
import { formatCitation } from '../utils/citationFormatter';
import { getCitationConfig } from '../config/citationConfig';
import type { EditorView } from 'prosemirror-view';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';

const log = createFileLogger('LIBRARY_DOCS_PLUGIN');

/**
 * Helper: Get citation data from Y.js Map
 * Citations store their data in Y.js Map (like comments) to avoid mark attribute issues
 */
export function getCitationData(ydoc: any, citationId: string): {
  referenceIds: string[];
  formattedText: string;
  references: Array<{
    id: string;
    title: string;
    authors: string[];
    year: string | number;
    journal?: string;
    volume?: string;
    issue?: string;
    pages?: string;
    doi?: string;
    url?: string;
    primaryPdfId?: string;
  }>;
  createdAt: number;
} | null {
  try {
    if (!ydoc) return null;
    const citationsMap = ydoc.getMap('citations');
    const citationData = citationsMap.get(citationId);
    return citationData || null;
  } catch {
    return null;
  }
}

/**
 * Insert citation at current cursor position
 * This is the plugin's internal command for inserting citations
 */
export function insertCitation(view: EditorView, references: ReferenceEntity[], ydoc: any, docId?: string): boolean {
  if (!view || !view.state.schema.marks.citation) {
    console.error('[LibraryDocsPlugin] Citation mark not available in schema');
    return false;
  }
  
  // Format citation text directly from passed references (no re-fetch needed)
  (async () => {
    try {
      // Check if view is still valid
      if (!view || !view.state) {
        console.error('[LibraryDocsPlugin] Editor view no longer available');
        return;
      }
      
      // Get citation configuration for this document
      const config = getCitationConfig();
      
      // Format citation using configured style
      const citationText = formatCitation(references, config.style);
      
      console.log('[LibraryDocsPlugin] Formatted citation:', {
        citationText,
        style: config.style,
        referenceCount: references.length,
        references: references.map(r => ({
          id: r._id,
          authors: r.skeleton?.authors,
          year: r.skeleton?.year
        }))
      });
      
      const citationId = `cite-${Date.now()}`;
      const { state, dispatch } = view;
      const { schema } = state;
      const { from, to } = state.selection;
      
      // Use proper citation mark with citationId attribute (avoiding naming conflict with node.attrs.id)
      const citationMark = schema.marks.citation;
      if (!citationMark) {
        console.error('[LibraryDocsPlugin] Citation mark not found in schema');
        return;
      }
      
      // Extract reference IDs
      const referenceIds = references.map(r => r._id);
      
      // Store citation metadata in Y.js Map for hover/click functionality
      if (ydoc) {
        try {
          const citationsMap = ydoc.getMap('citations');
          
          const citationData = {
            referenceIds,
            formattedText: citationText,
            references: references.map(r => {
              console.log('[LibraryDocsPlugin] Storing reference in citations Map:', {
                id: r._id,
                name: r.name,
                skeleton: r.skeleton
              });
              
              return {
                id: r._id,
                title: r.skeleton?.title || r.name,
                authors: r.skeleton?.authors || [],
                year: r.skeleton?.year,
                journal: r.skeleton?.journal,
                volume: r.skeleton?.volume,
                issue: r.skeleton?.issue,
                pages: r.skeleton?.pages,
                doi: r.skeleton?.doi,
                url: r.skeleton?.url,
                primaryPdfId: r.skeleton?.primaryPdfId
              };
            }),
            createdAt: Date.now()
          };
          
          console.log('[LibraryDocsPlugin] Full citation data to store:', citationData);
          
          citationsMap.set(citationId, citationData);
          log.info('[LibraryDocsPlugin] Citation metadata stored');
        } catch (error) {
          log.error('[LibraryDocsPlugin] Failed to store citation metadata:', error);
        }
      }
      
      // Apply citation mark
      const mark = citationMark.create({
        citationId: citationId,
        resolved: false
      });
      
      // Insert text with mark
      const tr = state.tr
        .insertText(citationText, from, to)
        .addMark(from, from + citationText.length, mark);
      
      dispatch(tr);
      
      log.info('[LibraryDocsPlugin] Citation inserted:', { 
        citationId, 
        referenceIds: references.map(r => r._id) 
      });
      
      // Track citation relationship (if docId provided)
      if (docId && referenceIds.length > 0) {
        try {
          await fetchService(`${API_BASE_URL}/citations/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              doc_id: docId,
              pdf_ids: referenceIds,
              citation_context: citationText
            })
          });
          log.info('[LibraryDocsPlugin] Citation tracking updated for', referenceIds.length, 'PDFs');
        } catch (trackingError) {
          // Non-critical - citation was inserted successfully
          log.error('[LibraryDocsPlugin] Failed to track citation (non-critical):', trackingError);
        }
      } else if (!docId) {
        log.warn('[LibraryDocsPlugin] No docId provided, skipping citation tracking');
      }
    } catch (error) {
      console.error('[LibraryDocsPlugin] Failed to insert citation:', error);
    }
  })();
  
  return true;
}

/**
 * Get ProseMirror plugins to inject into editor
 * Call this when building editor plugins
 */
export function getLibraryDocsProseMirrorPlugins(ydoc: any) {
  return [
    citationTooltipPlugin(ydoc),
    citationCleanupPlugin(ydoc)
  ];
}

/**
 * Initialize the Library Docs plugin
 * Call this before DocsApp creates the ProseMirror editor
 */
export function initializeLibraryDocsPlugin() {
  console.log('[LibraryDocsPlugin] Initializing plugin...');
  
  // Register citation mark with schema
  schemaExtensionRegistry.registerMark('citation', citationMarkSpec);
  
  // Register bibliography node with schema
  schemaExtensionRegistry.registerNode('bibliography', bibliographyNodeSpec);
  
  console.log('[LibraryDocsPlugin] Schema extensions registered:', {
    registeredMarks: schemaExtensionRegistry.getRegisteredMarks(),
    registeredNodes: schemaExtensionRegistry.getRegisteredNodes(),
    hasCitation: schemaExtensionRegistry.getRegisteredMarks().includes('citation'),
    hasBibliography: schemaExtensionRegistry.getRegisteredNodes().includes('bibliography')
  });
  
  log.info('[LibraryDocsPlugin] Citation mark and bibliography node registered');
  
}


